import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { ListPagination } from '@/components/ListPagination';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { format, startOfMonth, subMonths, endOfMonth, getISOWeek } from 'date-fns';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, businessWeekBounds,
    formatBusinessDate,
} from '@/lib/format';
import { Search, Calendar } from 'lucide-react';
import SectionDrawer from '@/components/navigation/SectionDrawer';



interface WeeklyPayoutRow {
    week_start: string;
    due_date: string;
    vehicle_count: number;
    total_repayments: number;
    total_partner_amount: number;
    payout_amount: number;
    payout_id: number | null;
    payout_transaction_id: number | null;
    payout_created_at: string | null;
    payout_description: string | null;
    status: 'paid' | 'partial' | 'due' | 'upcoming';
    repayment_ids: number[];
}

const formatCurrency = (amount: number) =>
    'GHS' + new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

export default function Payouts() {
    const { partnerId, signOut } = useAuth();
    const navigate = useNavigate();
    const PAGE_SIZE = 15;
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Reset page on filter changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, monthRange.from, monthRange.to]);

    // Fetch weekly payouts from server-side RPC
    const { data: weeklyPayouts, isLoading } = useQuery({
        queryKey: ['partner-weekly-payouts', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_partner_weekly_payouts', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return (data as WeeklyPayoutRow[]) || [];
        },
        enabled: !!partnerId,
    });

    // Payout summary for stat cards
    const { data: payoutSummary, isLoading: summaryLoading } = useQuery({
        queryKey: ['partner-payout-summary', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_payout_summary', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return data?.[0] as {
                total_expected_all_time: number;
                total_received_all_time: number;
                outstanding_payouts: number;
                weeks_with_repayments: number;
                avg_weekly_partner_portion: number;
                earning_vehicles: number;
                collection_rate: number;
            } | null;
        },
        enabled: !!partnerId,
    });



    // Filter by search
    const filteredPayouts = useMemo(() => {
        if (!weeklyPayouts) return [];
        if (!debouncedSearch) return weeklyPayouts;
        const q = debouncedSearch.toLowerCase();
        return weeklyPayouts.filter(w => {
            const { weekStart, weekEnd } = businessWeekBounds(w.week_start);
            const weekLabel = format(weekStart, 'MMM d') + ' – ' + format(weekEnd, 'MMM d, yyyy');
            return weekLabel.toLowerCase().includes(q)
                || w.status.toLowerCase().includes(q);
        });
    }, [weeklyPayouts, debouncedSearch]);

    // Pagination
    const totalPages = Math.ceil(filteredPayouts.length / PAGE_SIZE);
    const paginatedWeeks = filteredPayouts.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'paid':
                return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
            case 'partial':
                return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>;
            case 'due':
                return <Badge className="bg-red-100 text-red-700 border-red-200">No payments</Badge>;
            case 'upcoming':
                return <Badge variant="outline" className="text-muted-foreground">Upcoming</Badge>;
            default:
                return null;
        }
    };

    const navigateToWeek = (weekKey: string) => {
        const params = new URLSearchParams();
        params.set('week', weekKey);
        params.set('partner', String(partnerId));
        navigate(`/finance/payouts/week?${params.toString()}`);
    };

    const handleSignOut = async () => { await signOut(); navigate('/auth'); };
    const handleNavChange = (section: string) => { navigate(`/${section}`); };


    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer activeSection="finance/payouts" onSectionChange={handleNavChange} onSignOut={handleSignOut} />
            </div>



            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight mb-2">Payouts</h1>
                    <p className="text-muted-foreground">Track your weekly payout history.</p>
                </div>
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Stat Cards */}
            {summaryLoading ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
            ) : (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    <StatCard title="Total Expected" value={<CurrencyDisplay amount={payoutSummary?.total_expected_all_time ?? 0} />} description="Your total earnings from all repayments" />
                    <StatCard title="Payouts Received" value={<CurrencyDisplay amount={payoutSummary?.total_received_all_time ?? 0} />} description="Total amount paid to your account" />
                    <StatCard title="Outstanding" value={<CurrencyDisplay amount={payoutSummary?.outstanding_payouts ?? 0} />} description="Earned but not yet paid out to you" />
                    <StatCard title="Collection Rate" value={`${(payoutSummary?.collection_rate ?? 0).toFixed(2)}%`} description="How much drivers paid vs. what was expected weekly" />
                    <StatCard title="Earning Vehicles" value={payoutSummary?.earning_vehicles ?? 0} description="Vehicles actively generating repayments" />
                </div>
            )}

            <Card className="border-0">
                <CardContent className="space-y-4">
                    {/* Search + Pagination */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by week..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        {totalPages > 1 && (
                            <ListPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={filteredPayouts.length}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>

                    {/* Weekly Payouts */}
                    {isLoading ? (
                        <Skeleton className="h-48 w-full" />
                    ) : paginatedWeeks.length > 0 ? (
                        <>
                            {/* Desktop table */}
                            <div className="hidden lg:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[220px]">Week</TableHead>
                                            <TableHead>Vehicles</TableHead>
                                            <TableHead>Earnings</TableHead>
                                            <TableHead>Payout</TableHead>
                                            <TableHead>Payout Date</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedWeeks.map((week) => {
                                            const { weekStart, weekEnd, weekStartYmd: weekKey } = businessWeekBounds(week.week_start);
                                            return (
                                                <TableRow
                                                    key={weekKey}
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => navigateToWeek(weekKey)}
                                                >
                                                    <TableCell className="whitespace-nowrap">
                                                        <div className="font-medium">{format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}</div>
                                                        <div className="text-xs text-muted-foreground">Week {getISOWeek(weekStart)}</div>
                                                    </TableCell>
                                                    <TableCell>{week.vehicle_count}</TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {formatCurrency(week.total_partner_amount)}
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {week.payout_amount > 0
                                                            ? formatCurrency(week.payout_amount)
                                                            : <span className="text-muted-foreground font-normal">—</span>
                                                        }
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {week.payout_created_at
                                                            ? formatBusinessDate(week.payout_created_at)
                                                            : <span>—</span>
                                                        }
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(week.status)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile cards */}
                            <div className="lg:hidden divide-y border rounded-lg">
                                {paginatedWeeks.map((week) => {
                                    const { weekStart, weekEnd, weekStartYmd: weekKey } = businessWeekBounds(week.week_start);
                                    return (
                                        <div
                                            key={weekKey}
                                            className="p-4 space-y-2 cursor-pointer hover:bg-muted/50 active:bg-muted/70"
                                            onClick={() => navigateToWeek(weekKey)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-muted-foreground">
                                                    {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d')}
                                                </span>
                                                {getStatusBadge(week.status)}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="text-xs text-muted-foreground">{week.vehicle_count} vehicle(s)</div>
                                                    <div className="font-medium">{formatCurrency(week.total_partner_amount)}</div>
                                                </div>
                                                {week.payout_amount > 0 && (
                                                    <div className="text-right">
                                                        <div className="text-xs text-muted-foreground">Paid</div>
                                                        <div className="font-medium text-green-600">
                                                            {formatCurrency(week.payout_amount)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <EmptyState
                            icon={<Calendar />}
                            title="No payout data"
                            description="No payout data available for the selected period. Try adjusting the date range."
                        />
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
